import { useCallback, useEffect, useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Landmark, Wallet, PiggyBank, Hourglass, Loader2, RefreshCw, Info, ChevronDown, Banknote } from "lucide-react";

/**
 * StoreRollup — on-page mirror of the daily email.
 *
 * Shows the same per-store × 4-bucket breakdown that gets sent by email:
 *   • Cleared to bank
 *   • In Stripe balance (settling)
 *   • Paid on credit
 *   • Still processing
 *
 * Backend endpoint `POST /reports/store-rollup` returns the same structured
 * data that powers the email, so keeping the two views in sync is free.
 */

const BUCKETS = [
  {
    key: "paid_out",
    title: "Paid out to bank",
    accent: "#1b6b2f",
    tint: "bg-[#e4f1e8] text-[#1b6b2f] border-[#bcdcc3]",
    icon: Banknote,
    countKey: "paid_out_count",
    amountKey: "paid_out_amount",
    what: "Money Stripe actually deposited into your bank account via a payout that landed in this window. This is cash in hand.",
    when: "A payout's arrival date fell inside this report's window.",
  },
  {
    key: "cleared_to_bank",
    title: "In Stripe balance (cleared)",
    accent: "#2f7d3f",
    tint: "bg-[#eaf3ec] text-[#2f7d3f] border-[#cde3d1]",
    icon: Landmark,
    countKey: "cleared_count",
    amountKey: "cleared_amount",
    what: "Funds that cleared and became available for payout in your Stripe balance during this window — not yet swept to the bank. They move to 'Paid out' once the next payout lands.",
    when: "Cards: ~2 business days after the charge · ACH: ~5 business days.",
  },
  {
    key: "paid_via_stripe",
    title: "Settling in Stripe",
    accent: "#1f5060",
    tint: "bg-[#e5eef2] text-[#1f5060] border-[#c8dbe3]",
    icon: Wallet,
    countKey: "paid_stripe_count",
    amountKey: "paid_stripe_amount",
    what: "Card is authorized, captured, and the charge succeeded — but the funds haven't moved into your available balance yet. Stripe is holding it in 'pending' during the settlement window.",
    when: "Same-day card payments and the first ~2 days after any card charge.",
  },
  {
    key: "paid_credit",
    title: "Paid on credit / house account",
    accent: "#1f5f47",
    tint: "bg-[#e5efe9] text-[#1f5f47] border-[#c9ddd2]",
    icon: PiggyBank,
    countKey: "paid_credit_count",
    amountKey: "paid_credit_amount",
    what: "Covered entirely by house-account / internal credit — no Stripe payment for this order. Money never went through Stripe so there's nothing to clear.",
    when: "credit_applied ≥ total AND no Stripe activity on the order.",
  },
  {
    key: "in_progress",
    title: "Still processing",
    accent: "#7c5a14",
    tint: "bg-[#f5ecd5] text-[#7c5a14] border-[#e5d4a5]",
    icon: Hourglass,
    countKey: "in_progress_count",
    amountKey: "in_progress_amount",
    what: "Money isn't in your possession yet. Either an ACH is still clearing, a 3-D Secure step is pending, or Base44 shows an outstanding balance. Each row includes an explanation of what's holding it up.",
    when: "ACH before it clears · unpaid balance-due orders · failed retries · disputes.",
  },
];

export default function StoreRollup({ defaultDate }) {
  const [date, setDate] = useState(defaultDate || todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await api.post("/reports/store-rollup", { date: d });
      setData(data);
    } catch {
      /* silent — surface via UI empty state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [date, load]);

  const grand = data?.totals;
  const stores = data?.stores || [];

  return (
    <div className="surface-card p-6" data-testid="store-rollup">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="label-eyebrow">Per-store rollup</div>
          <h2 className="font-heading text-2xl font-semibold mt-1">
            Same view as the daily email
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-lg">
            Every store · every order that had activity · grouped by where the
            money is right now.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] label-eyebrow">Business day</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40 mt-1"
              data-testid="store-rollup-date"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(date)}
            disabled={loading}
            data-testid="store-rollup-refresh"
          >
            {loading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {grand && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
          {BUCKETS.map((b) => {
            const Icon = b.icon;
            const amount = grand[b.amountKey] || 0;
            const count = grand[b.countKey] || 0;
            return (
              <div
                key={b.key}
                className="border border-border/70 rounded-lg p-3 bg-secondary/20"
                style={{ borderTopColor: b.accent, borderTopWidth: 3 }}
                data-testid={`store-rollup-kpi-${b.key}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="label-eyebrow" style={{ color: b.accent }}>
                      {b.title}
                    </div>
                    <div className="font-heading text-xl font-bold mt-1 num">
                      {fmtCurrency(amount)}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {count} orders
                    </div>
                  </div>
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: b.accent }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StatusKey />

      {loading && !data ? (
        <div className="py-16 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : stores.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground border border-dashed border-border/60 rounded-lg bg-secondary/20">
          No order activity for this business day.
        </div>
      ) : (
        <div className="space-y-5">
          {stores.map((s) => (
            <StoreSection key={s.name} store={s} />
          ))}
        </div>
      )}
    </div>
  );
}

function StoreSection({ store }) {
  const t = store.totals;
  return (
    <div className="rounded-lg overflow-hidden border border-border/70" data-testid={`store-section-${store.name}`}>
      <div className="bg-[#2b2620] text-[#fbfaf6] px-4 py-3 flex items-center justify-between gap-3">
        <div className="font-heading font-bold text-lg tracking-tight">{store.name}</div>
        <div className="text-xs text-[#c6bcac]">
          {t.orders} orders · <span className="text-white font-semibold num">{fmtCurrency(t.total_revenue)}</span>
        </div>
      </div>

      <div className="bg-white divide-y divide-border/50">
        {BUCKETS.map((b) => {
          const rows = store[b.key] || [];
          if (rows.length === 0) return null;
          const count = t[b.countKey];
          const amount = t[b.amountKey];
          return (
            <BucketRows
              key={b.key}
              bucket={b}
              rows={rows}
              count={count}
              amount={amount}
            />
          );
        })}
        {BUCKETS.every((b) => (store[b.key] || []).length === 0) && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            No orders.
          </div>
        )}
      </div>
    </div>
  );
}

function BucketRows({ bucket, rows, count, amount }) {
  const Icon = bucket.icon;
  return (
    <div>
      <div
        className="flex items-center justify-between px-4 py-2 border-b-2"
        style={{ borderBottomColor: bucket.accent }}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: bucket.accent }} />
          <div
            className="text-[11px] tracking-[.18em] uppercase font-bold"
            style={{ color: bucket.accent }}
          >
            {bucket.title}
          </div>
        </div>
        <div className="text-[11px] text-muted-foreground num">
          {count} · {fmtCurrency(amount)}
        </div>
      </div>
      <ul className="divide-y divide-border/40">
        {rows.map((r, i) => (
          <li
            key={`${r.number}-${i}`}
            className="px-4 py-2.5 flex items-center gap-3 hover:bg-secondary/30 transition-colors"
          >
            <div className="font-mono text-xs w-24 shrink-0 truncate" title={r.number}>
              {r.number}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm truncate" title={r.customer_name}>
                  {r.customer_name}
                </span>
                {bucket.key === "in_progress" && r.state_label && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${bucket.tint}`}>
                    {r.state_label}
                  </span>
                )}
                {bucket.key === "paid_out" && r.arrival_on && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${bucket.tint}`}>
                    paid out {r.arrival_on}
                  </span>
                )}
                {bucket.key === "cleared_to_bank" && r.available_on && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${bucket.tint}`}>
                    available {r.available_on}
                  </span>
                )}
              </div>
              {bucket.key === "in_progress" && r.reason && (
                <div className="text-[11px] text-[#7c5a14] mt-0.5 truncate" title={r.reason}>
                  {r.reason}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                Sales: <span className="text-foreground">{r.sales_rep}</span>
                {r.po_number && (
                  <>
                    {" · "}PO <span className="font-mono">{r.po_number}</span>
                  </>
                )}
              </div>
            </div>
            <div className="num font-semibold text-sm">{fmtCurrency(r.total)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusKey() {
  // Default to open so anyone landing on the report immediately sees what
  // each bucket means. Users can collapse it after they've read it once.
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-6 border border-border/70 rounded-lg bg-secondary/10" data-testid="rollup-status-key">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-secondary/30 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">What do these statuses mean?</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-border/60">
          <div className="grid gap-3 md:grid-cols-2 mt-3">
            {BUCKETS.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.key}
                  className="border border-border/60 rounded-md p-3 bg-white"
                  style={{ borderLeftColor: b.accent, borderLeftWidth: 3 }}
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5" style={{ color: b.accent }} />
                    <div
                      className="text-[11px] tracking-[.18em] uppercase font-bold"
                      style={{ color: b.accent }}
                    >
                      {b.title}
                    </div>
                  </div>
                  <div className="text-xs text-foreground mt-2 leading-relaxed">{b.what}</div>
                  <div className="text-[11px] text-muted-foreground mt-1.5">
                    <span className="uppercase tracking-wider text-[10px] font-semibold">Typical timing:</span>{" "}
                    {b.when}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
